import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AlertConfig, AlertState } from '../../../shared/models/design-system.types';

/**
 * Alert Service
 *
 * Manages toast notifications throughout the application.
 *
 * Usage:
 * ```typescript
 * constructor(private alertService: AlertService) {}
 *
 * showSuccess() {
 *   this.alertService.success('Operation completed successfully');
 * }
 *
 * showError() {
 *   this.alertService.danger('Something went wrong', { title: 'Error' });
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class AlertService {
  private alerts = new BehaviorSubject<AlertState[]>([]);
  private idCounter = 0;

  /** Default duration in milliseconds */
  private defaultDuration = 5000;

  /** Maximum number of visible alerts */
  private maxAlerts = 5;

  /** Observable stream of active alerts */
  get alerts$(): Observable<AlertState[]> {
    return this.alerts.asObservable();
  }

  /**
   * Show a success alert
   */
  success(message: string, options?: Partial<AlertConfig>): string {
    return this.show({ type: 'success', message, ...options });
  }

  /**
   * Show a danger/error alert
   */
  danger(message: string, options?: Partial<AlertConfig>): string {
    return this.show({ type: 'danger', message, ...options });
  }

  /**
   * Alias for danger()
   */
  error(message: string, options?: Partial<AlertConfig>): string {
    return this.danger(message, options);
  }

  /**
   * Show a warning alert
   */
  warning(message: string, options?: Partial<AlertConfig>): string {
    return this.show({ type: 'warning', message, ...options });
  }

  /**
   * Show an info alert
   */
  info(message: string, options?: Partial<AlertConfig>): string {
    return this.show({ type: 'info', message, ...options });
  }

  /**
   * Show a custom alert
   */
  show(config: AlertConfig): string {
    const id = this.generateId();
    const alert: AlertState = {
      ...config,
      id,
      createdAt: Date.now(),
      dismissible: config.dismissible !== false, // Default to true
      duration: config.duration ?? this.defaultDuration
    };

    const currentAlerts = this.alerts.value;

    // Remove oldest alerts if we exceed max
    let updatedAlerts = [...currentAlerts, alert];
    if (updatedAlerts.length > this.maxAlerts) {
      updatedAlerts = updatedAlerts.slice(updatedAlerts.length - this.maxAlerts);
    }

    this.alerts.next(updatedAlerts);

    // Auto-dismiss if duration is set
    if (alert.duration && alert.duration > 0) {
      setTimeout(() => this.dismiss(id), alert.duration);
    }

    return id;
  }

  /**
   * Dismiss a specific alert by ID
   */
  dismiss(id: string): void {
    const currentAlerts = this.alerts.value;
    this.alerts.next(currentAlerts.filter(alert => alert.id !== id));
  }

  /**
   * Dismiss all alerts
   */
  dismissAll(): void {
    this.alerts.next([]);
  }

  /**
   * Update the default duration
   */
  setDefaultDuration(ms: number): void {
    this.defaultDuration = ms;
  }

  /**
   * Update the maximum number of visible alerts
   */
  setMaxAlerts(count: number): void {
    this.maxAlerts = count;
  }

  private generateId(): string {
    return `alert-${++this.idCounter}-${Date.now()}`;
  }
}
